import { useAction, useMutation, useQuery } from "convex/react";
import { useCallback } from "react";

import { api } from "@convex/_generated/api";
import {
  notebookInvalidPath,
  notebookPublishFailed,
  notebookShareNoteMissing,
  notebookShareTooLarge,
  notebookUnpublishFailed,
} from "@domain/user-errors";
import { logMutationError } from "@/lib/logger";

/**
 * The single hook surface for notebook publish-to-web shares (notebook-shares
 * spec §3 D4) — components stay Convex-free. Replaces the localStorage stub
 * (`lib/notebookSharesStub.ts`, deleted): shares now live in the
 * `notebook_shares` table, so a link works for ANY visitor, not just the
 * publisher's browser.
 *
 * `publish` sends ONLY `{ path }` — the server reads the note body from the
 * caller's own vault (spec C2); clients never supply publish content.
 */

/**
 * C7: the stub persisted FULL note content at rest under this key. One-time
 * purge on module init so pre-swap visitors don't keep stale snapshots in
 * localStorage.
 */
const LEGACY_SHARE_STORE_KEY = "clawcraft-notebook-shares";
try {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(LEGACY_SHARE_STORE_KEY);
  }
} catch {
  // Storage unavailable (private mode / SSR) — nothing to purge.
}

/** Owner's view of one note's share state (`getForDoc`). */
export interface ShareForDoc {
  slug: string;
  publishedAt: number;
}

/** The public snapshot payload (`getBySlug`) — never carries owner identity. */
export interface PublicShare {
  title: string;
  content: string;
  publishedAt: number;
}

/** Thrown by publish/unpublish so callers surface copy inline. */
export class NotebookShareError extends Error {
  readonly userMessage: string;
  readonly reason: unknown;
  constructor(userMessage: string, reason: unknown) {
    super(userMessage);
    this.name = "NotebookShareError";
    this.userMessage = userMessage;
    this.reason = reason;
  }
}

function publishErrorCopy(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error);
  if (msg.includes("SHARE_TOO_LARGE")) return notebookShareTooLarge();
  if (msg.includes("NOT_FOUND")) return notebookShareNoteMissing();
  if (msg.includes("INVALID_NOTE_PATH")) return notebookInvalidPath();
  return notebookPublishFailed();
}

/**
 * Live share state for one note path; `null` path skips (menu closed).
 * `undefined` = loading, `null` = not published.
 */
export function useShareForDoc(path: string | null): ShareForDoc | null | undefined {
  return useQuery(api.notebookShares.getForDoc, path === null ? "skip" : { path });
}

/**
 * Public snapshot lookup for the `/share/$slug` route — unauthenticated by
 * design (the slug is the capability). `undefined` = loading, `null` = not
 * available (malformed slug, revoked, or never published).
 */
export function useShareBySlug(slug: string): PublicShare | null | undefined {
  return useQuery(api.notebookShares.getBySlug, { slug });
}

/** Publish (or republish) a note. Sends ONLY the path (spec C2). */
export function usePublishShare() {
  const publishAction = useAction(api.notebookShareActions.publish);
  return useCallback(
    async ({ path }: { path: string }): Promise<{ slug: string }> => {
      try {
        return await publishAction({ path });
      } catch (err) {
        logMutationError({
          mutation: "notebookShareActions.publish",
          args: { path },
          error: err,
        });
        throw new NotebookShareError(publishErrorCopy(err), err);
      }
    },
    [publishAction],
  );
}

/** Revoke a note's share. Idempotent server-side. */
export function useUnpublishShare() {
  const unpublishMutation = useMutation(api.notebookShares.unpublish);
  return useCallback(
    async ({ path }: { path: string }): Promise<void> => {
      try {
        await unpublishMutation({ path });
      } catch (err) {
        logMutationError({
          mutation: "notebookShares.unpublish",
          args: { path },
          error: err,
        });
        throw new NotebookShareError(notebookUnpublishFailed(), err);
      }
    },
    [unpublishMutation],
  );
}
